/**
 * useAsyncOperation Hook
 *
 * 管理异步操作的状态，提供统一的接口处理加载、错误和结果
 *
 * 特性：
 * 1. 统一管理异步操作状态（加载、错误、结果）
 * 2. 集成错误处理服务
 * 3. 支持取消操作和自动清理
 * 4. 防止内存泄漏（组件卸载后不更新状态）
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { ErrorType, ErrorSeverity } from '@readsavor/types';
// 直接从错误模块导入
import { ErrorHandlerService } from '../../error/service/index.js';
import { useErrorHandler } from '../../error/hooks/index.js';
/**
 * 安全地获取错误处理程序，如果可用则使用Context中的处理程序，否则使用单例
 * @returns 错误处理服务实例
 */
function useSafeErrorHandler() {
    try {
        // 尝试使用useErrorHandler，但不在这个自定义Hook内部抛出错误
        return useErrorHandler();
    }
    catch (e) {
        // 如果不在ErrorProvider上下文中，返回null
        return null;
    }
}
/**
 * 管理异步操作状态的hook
 * @template T 操作结果类型
 * @template E 错误类型
 * @param asyncFunction 异步函数
 * @param options 配置选项
 * @returns 异步操作控制器
 */
export function useAsyncOperation(asyncFunction, options = {}) {
    // 在Hook的顶层调用，避免条件式调用Hook
    const errorHandlerFromContext = useSafeErrorHandler();
    // 如果上下文中没有错误处理程序，则使用单例
    const errorHandler = errorHandlerFromContext || ErrorHandlerService.getInstance();
    // 默认选项
    const { autoExecute = false, resetOnExecute = true, errorType = ErrorType.UNEXPECTED, errorSeverity = ErrorSeverity.MEDIUM, component = 'useAsyncOperation', userErrorMessage, useErrorService = true, } = options;
    // 初始状态 - 使用useMemo缓存，避免每次渲染创建新对象
    const initialState = useMemo(() => ({
        isLoading: false,
        error: null,
        result: null,
        isSuccess: false,
        timestamp: null,
    }), []);
    // 创建状态
    const [state, setState] = useState(initialState);
    // 组件是否已卸载的引用，防止内存泄漏
    const isMounted = useRef(true);
    // 操作是否被取消的引用
    const isCancelled = useRef(false);
    const setLoading = useCallback((isLoading) => {
        if (isMounted.current) {
            setState(prevState => ({
                ...prevState,
                isLoading,
            }));
        }
    }, []);
    // 重置状态
    const reset = useCallback(() => {
        if (isMounted.current) {
            setState(initialState);
        }
    }, [initialState]);
    // 手动设置结果
    const setResult = useCallback((result) => {
        if (isMounted.current) {
            setState({
                isLoading: false,
                error: null,
                result,
                isSuccess: true,
                timestamp: Date.now(),
            });
        }
    }, []);
    // 手动设置错误
    const setError = useCallback((error) => {
        if (isMounted.current) {
            if (useErrorService && errorHandler) {
                errorHandler.handleError(error, {
                    type: errorType,
                    component,
                    operation: 'asyncOperation',
                    severity: errorSeverity,
                    userMessage: userErrorMessage,
                });
            }
            setState({
                isLoading: false,
                error,
                result: null,
                isSuccess: false,
                timestamp: Date.now(),
            });
        }
    }, [errorType, component, errorSeverity, userErrorMessage, errorHandler, useErrorService]);
    // 执行异步操作
    const execute = useCallback(async (...args) => {
        // 取消之前的操作
        isCancelled.current = false;
        // 重置状态（如有配置）
        if (resetOnExecute) {
            setState(_prev => ({
                ...initialState,
                isLoading: true,
            }));
        }
        else {
            setState(_prev => ({
                ..._prev,
                isLoading: true,
                error: null,
            }));
        }
        try {
            // 执行异步函数
            const result = await asyncFunction(...args);
            // 检查是否已取消或卸载
            if (!isCancelled.current && isMounted.current) {
                setState({
                    isLoading: false,
                    error: null,
                    result,
                    isSuccess: true,
                    timestamp: Date.now(),
                });
            }
            return result;
        }
        catch (error) {
            // 处理错误
            if (!isCancelled.current && isMounted.current) {
                // 记录错误
                if (useErrorService && errorHandler) {
                    errorHandler.handleError(error, {
                        type: errorType,
                        component,
                        operation: 'asyncOperation',
                        data: { args },
                        severity: errorSeverity,
                        userMessage: userErrorMessage,
                    });
                }
                setState({
                    isLoading: false,
                    error: error,
                    result: null,
                    isSuccess: false,
                    timestamp: Date.now(),
                });
            }
            return null;
        }
    }, [
        asyncFunction,
        resetOnExecute,
        errorType,
        component,
        errorSeverity,
        userErrorMessage,
        errorHandler,
        useErrorService,
        initialState,
    ]);
    // 自动执行（如有配置）
    useEffect(() => {
        if (autoExecute) {
            execute();
        }
        // 清理函数
        return () => {
            isMounted.current = false;
            isCancelled.current = true;
        };
    }, [autoExecute, execute]);
    return {
        state,
        execute,
        reset,
        setResult,
        setError,
        setLoading,
        isActive: !isCancelled.current,
    };
}
//# sourceMappingURL=useAsyncOperation.js.map